import { useState, useEffect } from 'react';

export const useUserData = (userEmail, type = 'idcard') => {
  const [userData, setUserData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchUserData = async () => {
      if (!userEmail) {
        setError('User email is required');
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError(null);

        // API endpoints for Strapi backend
        const apiUrl = type === 'vc' 
          ? `${process.env.REACT_APP_API_URL}/external-data/visiting-card/${userEmail}` 
          : `${process.env.REACT_APP_API_URL}/external-data/id-card/${userEmail}`;

        const response = await fetch(apiUrl);
        
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const data = await response.json();
        
        if (data.success) {
          setUserData(data.data);
        } else {
          setError(data.message || 'Failed to fetch user data');
        }
      } catch (err) {
        setError(err.message || 'Failed to fetch user data');
      } finally {
        setLoading(false);
      }
    };

    fetchUserData();
  }, [userEmail, type]);

  const refetch = () => {
    if (userEmail) {
      setLoading(true);
      setError(null);
      // Re-run the useEffect by creating a new fetch
      const fetchUserData = async () => {
        try {
          setLoading(true);
          setError(null);

          // API endpoints for Strapi backend
          const apiUrl = type === 'vc' 
            ? `${process.env.REACT_APP_API_URL}/external-data/visiting-card/${userEmail}` 
            : `${process.env.REACT_APP_API_URL}/external-data/id-card/${userEmail}`;

          const response = await fetch(apiUrl);
          
          if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
          }

          const data = await response.json();
          
          if (data.success) {
            setUserData(data.data);
          } else {
            setError(data.message || 'Failed to fetch user data');
          }
        } catch (err) {
          setError(err.message || 'Failed to fetch user data');
        } finally {
          setLoading(false);
        }
      };
      
      fetchUserData();
    }
  };

  return { userData, loading, error, refetch };
};

// Alternative hook for local/mock data during development
export const useMockUserData = (userEmail) => {
  const [userData, setUserData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    // Simulate API call with mock data
    const timeout = setTimeout(() => {
      if (userEmail) {
        // Mock user data - replace with your actual data structure
        const mockData = {
          user_name: 'John Doe',
          designation: 'Software Engineer',
          employee_id: 'BA-001',
          blood_grp: 'A+',
          user_phone: '+880-1234567890',
          user_email: `${userEmail}@ba-systems.com`,
          user_pic: null,
          user_emergency_contact: '+880-9876543210',
          office_contact: '+880-9602-666777',
          office_address: 'Dhaka, Bangladesh'
        };
        
        setUserData(mockData);
        setError(null);
      } else {
        setError('User email is required');
      }
      setLoading(false);
    }, 1000);

    return () => clearTimeout(timeout);
  }, [userEmail]);

  return { userData, loading, error };
};